import * as base64 from 'base-64';
import { IApiInterface } from './IApiConfig';
import { IAuthTokenBuilder } from './IAuthTokenBuilder';

export interface IRequestOptions {
  method: string;
  headers: Headers;
  body: undefined|string;
  credentials?: string;
}

export interface IRequest {
  url: string;
  options: IRequestOptions;
}

export const UnauthorizedErrorMessage = '401: Unauthorized';

export class BaseApiClient {
  protected fetch: any;
  protected config: IApiInterface;
  protected apiBaseUrl: string;

  public constructor(config: IApiInterface, fetch: any,
                     private authTokenBuilder: IAuthTokenBuilder|undefined,
                     private googleAnalytics?: any) {
    this.fetch = fetch;
    this.config = config;
    this.apiBaseUrl = (this.config.API_BASE_URL || '').replace(/\/$/, '');
  }

  protected buildRequest = async (path: string, method: string, localServer: boolean = false): Promise<IRequest> => {
    if (localServer) {
      // connect to the gym
      return {
        url: `${this.config.LOCAL_SERVER_BASE_URL}/${path}`,
        options: {
          method,
          body: undefined,
          headers: this.getHeaders(),
        },
      };
    }

    const headers = await this.getTokenBasedAuthHeaders();

    return {
      url: `${this.apiBaseUrl}/${path}`,
      options: {
        method,
        body: undefined,
        headers,
      },
    };
  }

  protected buildGetRequest = async (path: string, localServer: boolean = false): Promise<IRequest> => {
    return this.buildRequest(path, 'GET', localServer);
  }

  protected buildPostRequest = async (path: string, data: any, localServer: boolean = false): Promise<IRequest> => {
    const request = await this.buildRequest(path, 'POST', localServer);
    request.options.body = JSON.stringify(data);
    return request;
  }

  protected buildDeleteRequest = async (path: string, localServer: boolean = false): Promise<IRequest> => {
    return await this.buildRequest(path, 'DELETE', localServer);
  }

  protected buildPutRequest = async (path: string, data: any, localServer: boolean = false): Promise<IRequest> => {
    const request =  await this.buildRequest(path, 'PUT', localServer);
    request.options.body = JSON.stringify(data);
    return request;
  }

  protected get = async (path: string): Promise<any> => {
    const request = await this.buildGetRequest(path);
    const result = await this.fetch(request.url, request.options);
    return await this.assertHttp200AndGetJsonFromResult(result);
  }

  protected put = async (path: string, data: any): Promise<any> => {
    const request = await this.buildPutRequest(path, data);
    const result = await this.fetch(request.url, request.options);
    return await this.assertHttp200AndGetJsonFromResult(result);
  }

  protected getHeaders = (
    username = this.config.LOCAL_SERVER_USERNAME,
    password = this.config.LOCAL_SERVER_PASSWORD) => {
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      const authToken = base64.encode(`${username}:${password}`);
      headers.append('Authorization', `Basic ${authToken}`);
      headers.append('X-App-Version', `${this.config.PLATFORM} ${this.config.VERSION_NUMBER}`);
      return headers;
  }

  protected getTokenBasedAuthHeaders = async () => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    if (this.authTokenBuilder) {
      const token = await this.authTokenBuilder.getAuthorizationHeader();
      headers.append('Authorization', token);
      // add X-Authorization header also, since safari overwrites the Authorization header when the site
      // was loaded with basic auth
      headers.append('X-Authorization', token);
    }
    headers.append('X-App-Version', `${this.config.PLATFORM} ${this.config.VERSION_NUMBER}`);
    return headers;
  }

  protected assertHttp200AndGetJsonFromResult = async (result: any): Promise<any> => {
    if (result.status !== 200) {
      const NoJsonData = '(no json data returned)';
      let message = NoJsonData;
      try {
        const data = await result.json();
        message = data.message || message;
      } catch (err) { /* ignored */ }
      if (result.status === 401 && message === NoJsonData) {
        throw new Error(UnauthorizedErrorMessage);
      }

      const errorMessage = result.status >= 500 ? 'Oops, something went wrong. Please try again.' : message;
      throw new Error(errorMessage);
    }
    return await result.json();
  }

  protected assertHttp200AndGetJsonFromResultOrNullFor404 = async (result: any): Promise<any> => {
    if (result.status === 404) {
      return null;
    }
    return this.assertHttp200AndGetJsonFromResult(result);
  }

  protected sendAnalyticsEvent = (action: string, category: string, label: any) => {
    if (!this.googleAnalytics) {
      return;
    }
    this.googleAnalytics.logEvent(action, category, label);
  }

  protected sendAnalyticsEventAndParams = (action: string, params: any) => {
    if (!this.googleAnalytics) {
      return;
    }
    this.googleAnalytics.logEventWithParamaters(action, params);
  }

  protected buildAnalyticsDate = (utcDate: number, workoutType: number) => {
    const date = new Date(utcDate);
    const dayOfWeek = date.toLocaleString('en', {weekday: 'long'}).substring(0, 3);
    const dateString = `${date.getDay()}${date.getMonth() + 1}${date.getFullYear()}`;
    return `${dayOfWeek}_${date.toLocaleTimeString()
      .replace(/:/g, '')}_${dateString}_${workoutType}`;
  }

  protected buildFetchOptionsWithCredentialsInclude(requestOptions: IRequestOptions): IRequestOptions {
    const headers: Headers = requestOptions.headers;
    headers.delete('Authorization');
    headers.delete('X-Authorization');

    return {
      headers,
      body: requestOptions.body,
      method: requestOptions.method,
      credentials: 'include',
    };
  }

  protected buildFetchOptionsWithUserProvidedAuth(requestOptions: IRequestOptions): IRequestOptions {
    // the auth header is already built in buildRequest above, using LOCAL_SERVER_* values. use those
    // headers here (only validate)
    if (!this.config.LOCAL_SERVER_USERNAME || !this.config.LOCAL_SERVER_PASSWORD) {
      throw new Error('Unauthorized: user provided authentication config missing');
    }

    return {
      ...requestOptions,
      credentials: 'include',
    };
  }
}
